import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { createBooking } from "@/lib/apiClient";
import { Loader2 } from "lucide-react";

const field =
  "w-full bg-[var(--field-bg)] border border-line text-ink font-sans text-sm px-4 py-3 focus:border-gold focus:outline-none transition-colors duration-300 placeholder:text-ink-dim";

export default function BookingDialog({ domain, open, onOpenChange }) {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    setLoading(true);
    try {
      await createBooking({ domain_id: domain.id, domain_name: domain.name, ...form });
      setDone(true);
      toast.success("Inquiry received. Our brokers will be in touch privately.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setDone(false); setForm({ full_name: "", email: "", phone: "", message: "" }); }, 300);
  };

  if (!domain) return null;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-testid="booking-dialog"
        className="bg-surface border border-gold/25 text-ink max-w-lg p-0 gap-0 rounded-2xl overflow-hidden"
      >
        <div className="p-8 md:p-10">
          {!done ? (
            <>
              <DialogHeader className="text-left space-y-3 mb-8">
                <div className="overline">Private Inquiry</div>
                <DialogTitle className="font-serif text-4xl tracking-tight text-ink">
                  {domain.name}
                </DialogTitle>
                <DialogDescription className="font-mono text-xs text-gold">
                  {domain.price} · {domain.category}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={submit} className="space-y-4" data-testid="booking-form">
                <input
                  className={field}
                  placeholder="Full name"
                  value={form.full_name}
                  onChange={set("full_name")}
                  data-testid="booking-name"
                />
                <input
                  className={field}
                  type="email"
                  placeholder="Email address"
                  value={form.email}
                  onChange={set("email")}
                  data-testid="booking-email"
                />
                <input
                  className={field}
                  placeholder="Phone (optional)"
                  value={form.phone}
                  onChange={set("phone")}
                  data-testid="booking-phone"
                />
                <textarea
                  className={`${field} min-h-[100px] resize-none`}
                  placeholder="Tell us about your intended use (optional)"
                  value={form.message}
                  onChange={set("message")}
                  data-testid="booking-message"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="booking-submit"
                  className="w-full bg-gold text-bg font-mono text-xs uppercase tracking-[0.24em] py-4 hover:bg-gold-muted hover:text-ink transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading && <Loader2 size={14} className="animate-spin" />}
                  Submit Inquiry
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-10" data-testid="booking-success">
              <div className="w-14 h-14 mx-auto rounded-full border border-gold flex items-center justify-center mb-8">
                <span className="text-gold-gradient font-serif text-3xl">✓</span>
              </div>
              <h3 className="font-serif text-3xl text-ink mb-3">Inquiry Received</h3>
              <p className="text-ink-dim text-sm max-w-xs mx-auto leading-relaxed">
                Your interest in <span className="font-mono text-gold">{domain.name}</span> has
                been logged. A broker will reach out confidentially within 24 hours.
              </p>
              <button
                onClick={() => close(false)}
                data-testid="booking-close"
                className="mt-8 font-mono text-xs uppercase tracking-[0.24em] text-ink hover-underline"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
